import { ReactTFC } from "../../../../../../utils/fp/missing-ts-parts";
import { CheckoutVisualButtonGroup } from "../../shared/CheckoutVisualButtonGroup";
import React from "react";
import { PaymentMethodT } from "../../data/available-payment-methods";
import { DualCheckoutVisualButton } from "./DualCheckoutVisualButton";
import { AdyenInstance } from "../../adyen/use-adyen-instance";
import { IssuersExtension } from "./IssuersExtension";
import { CreditCardPaymentMethod } from "./CreditCardPaymentMethod";
import { GooglePayPaymentMethod } from "./google-pay/GooglePayPaymentMethod";
import { PaymentNameWithIcon } from "./PaymentNameWithIcon";
import { useApplePayAvailability } from "./use-apple-pay-availability";
import { jsHooks } from "@swo/shared";
import { CheckoutSelectionState } from "../../state/type";
import { CheckoutPaymentMethodProps } from "../../adyen/types";
import { CCState, CheckoutPaymentMethodPropsTranslations } from "./types";
import { OpenedPaymentMethod } from "./CheckoutPaymentMethod";
import { SOPaymentMethods } from "@swo/storefront";

type CheckoutPaymentMethodWithAdyenProps = {
  disabled: boolean;

  options: SOPaymentMethods["availablePaymentMethods"];
  translations: CheckoutPaymentMethodPropsTranslations;
  clientKey: string;
  environment: "test" | "live";

  loadedAdyenInstance: AdyenInstance["underlying"];
  isApplePayAvailableOnThisDevice: boolean | undefined;

  openedPaymentMethod: OpenedPaymentMethod;
  setOpenedPaymentMethod: React.Dispatch<OpenedPaymentMethod>;
};

export const CheckoutPaymentMethodWithAdyen: ReactTFC<CheckoutPaymentMethodWithAdyenProps> = ({
  loadedAdyenInstance,
  openedPaymentMethod,
  setOpenedPaymentMethod,
  isApplePayAvailableOnThisDevice,
  ...props
}) => {
  return (
    <CheckoutVisualButtonGroup className={jsHooks.checkout.paymentMethods.list}>
      {props.options.map(paymentOption => {
        switch (paymentOption.tag) {
          case "credit-card": {
            return (
              <CreditCardPaymentMethod
                key={paymentOption.tag}
                // key={paymentOption.tag + (openedPaymentMethod?.tag === "credit-card").toString()}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                paymentOption={paymentOption}
                disabled={props.disabled}
                openedPaymentMethod={openedPaymentMethod}
                setOpenedPaymentMethod={setOpenedPaymentMethod}
                translations={props.translations}
                adyenInstance={loadedAdyenInstance}
              />
            );
          }

          case "paypal": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "paypal"}
                onClick={() => setOpenedPaymentMethod({ tag: "paypal" })}
                isOpened={openedPaymentMethod?.tag === "paypal"}
                Extension={null}
              >
                <PaymentNameWithIcon name={"paypal"} label={props.translations.payPal} />
              </DualCheckoutVisualButton>
            );
          }
          case "klarna_paynow": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "klarna_paynow"}
                onClick={() => setOpenedPaymentMethod({ tag: "klarna_paynow" })}
                isOpened={openedPaymentMethod?.tag === "klarna_paynow"}
                Extension={null}
              >
                <PaymentNameWithIcon name={"klarna_paynow"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }
          case "klarna": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "klarna"}
                onClick={() => setOpenedPaymentMethod({ tag: "klarna" })}
                isOpened={openedPaymentMethod?.tag === "klarna"}
                Extension={null}
              >
                <PaymentNameWithIcon name={"klarna"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }
          case "klarna_account": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "klarna_account"}
                onClick={() => setOpenedPaymentMethod({ tag: "klarna_account" })}
                isOpened={openedPaymentMethod?.tag === "klarna_account"}
                Extension={null}
              >
                <PaymentNameWithIcon name={"klarna_account"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }
          case "googlepay": {
            return (
              <GooglePayPaymentMethod
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                openedPaymentMethod={openedPaymentMethod}
                setOpenedPaymentMethod={setOpenedPaymentMethod}
                adyenInstance={loadedAdyenInstance}
                environment={props.environment}
                paymentOption={paymentOption}
                disabled={props.disabled}
              />
            );
          }
          case "applepay": {
            return isApplePayAvailableOnThisDevice ? (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "applepay"}
                onClick={() =>
                  setOpenedPaymentMethod({
                    tag: "applepay",
                    screen: {
                      tag: "selected",
                      data: {
                        merchantId: paymentOption.merchantId,
                        merchantName: paymentOption.merchantName,
                        brands: paymentOption.configuration.brands,
                      },
                    },
                  })
                }
                Extension={null}
                isOpened={openedPaymentMethod?.tag === "applepay"}
              >
                <PaymentNameWithIcon name={"applepay"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            ) : null;
          }
          case "eps": {
            const selected = (() => {
              if (openedPaymentMethod?.tag === "eps") {
                return openedPaymentMethod.screen.tag === "selected" ? openedPaymentMethod.screen.data : null;
              } else {
                return null;
              }
            })();

            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "eps"}
                isOpened={openedPaymentMethod?.tag === "eps"}
                onClick={() => setOpenedPaymentMethod({ tag: "eps", screen: { tag: "initialized" } })}
                Extension={
                  <IssuersExtension
                    key={selected?.id ?? "not-set"}
                    environment={props.environment}
                    issuers={paymentOption.issuers}
                    selected={selected}
                    onChange={v => {
                      const data = paymentOption.issuers.find(i => i.id === v);

                      if (data) {
                        setOpenedPaymentMethod({ tag: "eps", screen: { tag: "selected", data } });
                      } else {
                        console.warn(`Cannot find issuer with id ${v}`);
                      }
                    }}
                    editLabel={props.translations.creditCardEdit}
                  />
                }
              >
                <PaymentNameWithIcon name={"eps"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }

          case "directEbanking": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "directEbanking"}
                onClick={() => {
                  setOpenedPaymentMethod({ tag: "directEbanking" });
                }}
                Extension={null}
                isOpened={openedPaymentMethod?.tag === "directEbanking"}
              >
                <PaymentNameWithIcon name={"directEbanking"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }

          case "swish": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "swish"}
                onClick={() => {
                  setOpenedPaymentMethod({ tag: "swish" });
                }}
                Extension={null}
                isOpened={openedPaymentMethod?.tag === "swish"}
              >
                <PaymentNameWithIcon name={"swish"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }

          case "ideal": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "ideal"}
                isOpened={openedPaymentMethod?.tag === "ideal"}
                onClick={() => setOpenedPaymentMethod({ tag: "ideal", screen: { tag: "initialized" } })}
                Extension={
                  <IssuersExtension
                    key={(openedPaymentMethod?.tag === "ideal").toString()}
                    environment={props.environment}
                    issuers={paymentOption.issuers}
                    selected={(() => {
                      if (openedPaymentMethod?.tag === "ideal" && openedPaymentMethod.screen.tag === "selected") {
                        return openedPaymentMethod.screen.data;
                      } else {
                        return null;
                      }
                    })()}
                    onChange={v => {
                      const data = paymentOption.issuers.find(i => i.id === v);

                      if (data) {
                        setOpenedPaymentMethod({ tag: "ideal", screen: { tag: "selected", data } });
                      }
                    }}
                    editLabel={props.translations.creditCardEdit}
                  />
                }
              >
                <PaymentNameWithIcon name={"ideal"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }

          case "vipps": {
            return (
              <DualCheckoutVisualButton
                key={paymentOption.tag}
                className={jsHooks.checkout.paymentMethods.method(paymentOption.tag)}
                disabled={props.disabled}
                isSelected={openedPaymentMethod?.tag === "vipps"}
                onClick={() => setOpenedPaymentMethod({ tag: "vipps" })}
                Extension={null}
                isOpened={openedPaymentMethod?.tag === "vipps"}
              >
                <PaymentNameWithIcon name={"vipps"} label={paymentOption.name} />
              </DualCheckoutVisualButton>
            );
          }

          default: {
            const _: never = paymentOption;

            return _;
          }
        }
      })}
    </CheckoutVisualButtonGroup>
  );
};
